import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import {
  ClientAppEvent,
  EVOTRUX_BORDER_GREY,
  MAX_TFC_NOTES_LENGTH,
} from "constants/index";
import { useDisclosure, useWriterComponentState } from "hooks";

interface TfcRejectButtonProps {
  tfcId: number;
  onSuccess: () => void;
  undoConfirm?: boolean;
}

export function TfcRejectButton(props: TfcRejectButtonProps) {
  const { tfcId, onSuccess, undoConfirm = false } = props;
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const [notes, setNotes] = useState("");
  const posthog = usePostHog();

  const endpoint = undoConfirm ? "reopen" : "reject";
  const event = undoConfirm
    ? ClientAppEvent.TFC_REOPENED
    : ClientAppEvent.TFC_REJECTED;

  const { loading, write, error } = useWriterComponentState({
    errorMessage: "Failed to request changes. Please try again.",
    writeConfig: {
      method: "post",
      url: `/tfc/${tfcId}/${endpoint}`,
      body: { notes: notes || undefined },
    },
  });

  const handleSubmit = () => {
    onClose();
    write(() => {
      posthog?.capture(event);
      setNotes("");
      onSuccess();
    });
  };

  const handleClose = () => {
    setNotes("");
    onClose();
  };

  const buttonLabel = undoConfirm
    ? "UNDO ACCEPT & REQUEST CHANGES"
    : "REQUEST CHANGES";

  const dialogTitle = undoConfirm ? "Undo Confirmation" : "Request Changes";

  const dialogBody = undoConfirm
    ? "This will undo your confirmation and request changes from the carrier. You can provide notes below."
    : "Please describe what changes are needed. The carrier will be notified and can resubmit their costs.";

  const placeholder = undoConfirm
    ? "Describe why you are undoing the confirmation..."
    : "E.g., Please remove the layover charge as it was waived...";

  return (
    <>
      <Button
        variant="outlined"
        disabled={loading}
        onClick={onOpen}
        sx={{
          px: 3,
          color: undoConfirm ? "#1976D2" : "#D32F2F",
          borderColor: undoConfirm ? "#1976D2" : "#D32F2F",
          "&:hover": {
            borderColor: undoConfirm ? "#1565C0" : "#C62828",
            backgroundColor: undoConfirm
              ? "rgba(25, 118, 210, 0.04)"
              : "rgba(211, 47, 47, 0.04)",
          },
        }}
      >
        {buttonLabel}
      </Button>

      <ErrorTypography error={error} />

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 500, fontSize: "20px" }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>{dialogBody}</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={placeholder}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: MAX_TFC_NOTES_LENGTH }}
            helperText={`${notes.length}/${MAX_TFC_NOTES_LENGTH} characters`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleClose} sx={{ color: "#6B7280" }}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!undoConfirm && !notes.trim()}
            sx={{
              px: 3,
              backgroundColor: "#D32F2F",
              "&:hover": { backgroundColor: "#C62828" },
              "&.Mui-disabled": {
                backgroundColor: EVOTRUX_BORDER_GREY,
                color: "#9CA3AF",
              },
            }}
          >
            {undoConfirm ? "CONFIRM" : "REQUEST CHANGES"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
